import { Component, Input } from '@angular/core';
import { BasicCartesianChartConfig, BasicChartDataSource } from '@app/data/interface/widget-config';
import { take } from 'rxjs/operators';
import moment from 'moment-timezone';
import { EChartsOption, SeriesOption, TooltipComponentOption } from 'echarts';
import { forkJoin } from 'rxjs';
import { EnvironmentalDataSource } from '@app/data/services/types/EnvironmentalDataSource';
import {
  formatPrecipitation,
  getAnalyteProp as getAnalyteProp,
  weatherAnalyteId,
} from '@app/client/clients/wastemanagement/taiao-dashboard-layout/charts.config';
import { SeriesData } from 'echarts/types/dist/shared';
import { Analyte, EnvironmentalDataPoint, Granularity } from '@app/data/services/types/EnvironmentalData';
import { BaseFilteredEnvironmentalDataChartComponent } from '../base-filtered-environmental-data-chart-component';

/**
 * Tooltip formatter parameters for the tooltip.
 */
type TooltipFormatterParams = Parameters<Exclude<NonNullable<TooltipComponentOption['formatter']>, string>>[0];

/**
 * Axis pointer formatter parameters for the tooltip.
 */
type AxisPointerFormatterParams = {
  value: string | number;
  axisDimension: string;
  axisIndex: number;
  seriesData: SeriesData[];
};

/**
 * Enum for the series names in the wind barb chart.
 */
enum SeriesNames {
  Time = 'Time',
  Temperature = 'Temperature',
  Precipitation = 'Precipitation',
}

/**
 * Represents the response from the environmental data API.
 */
type EnvironmentalDataResponse = {
  analytes: Analyte[];
  data: EnvironmentalDataPoint[];
  environmentalDataSources: EnvironmentalDataSource[];
  granularity: Granularity;
};

/**
 * Dimension mappings for the wind barb chart.
 */
const chartDimensionMappings = {
  time: 'date_time',
  temperature: 'temperature',
  precipitation: 'precipitation',
};

/**
 * Interface for the dimension values in the chart
 */
interface WindBarbChartDimensions {
  date_time: string;
  temperature?: number;
  precipitation?: number;
}

@Component({
  selector: 'app-wind-barb-chart',
  templateUrl: './wind-barb-chart.component.html',
  styleUrls: ['./wind-barb-chart.component.scss'],
})
export class WindBarbChartComponent extends BaseFilteredEnvironmentalDataChartComponent {
  @Input() config!: BasicCartesianChartConfig;
  @Input() chartOptions: EChartsOption | null = null;

  ngOnInit() {
    super.ngOnInit();
    this.id = `wind-barb-chart-${this.config.dataConfig.widgetId}`;
    this.chartTitle = this.config.dataConfig.title || '';
    this.chartSubtitle = this.config.dataConfig.subtitle || '';
    this.domain = this.config.dataConfig.domain || '';

    // First load.
    this.setData();
  }

  /**
   * Clear any displayed chart data so the UI does not show stale results while
   * new data is being fetched. Implements the abstract contract from the base class.
   */
  protected clearChartData(): void {
    this.chartOptions = null;
    this.hasData = false;
  }

  /**
   * Formats the tooltip date_time value into a year string using moment.
   * @param value The time value to format.
   * @returns The formatted time string.
   */
  private formatTooltipTime = (value: number) => {
    return moment(value).format('DD/MM/YYYY HH:mm');
  };

  /**
   * Formats the tooltip value for the chart.
   *
   * If there is a missing value for a series, it will display '-'.
   *
   * @param seriesName - The name of the series.
   * @param value - The value to format.
   * @returns The formatted tooltip value.
   */
  private getFormattedTooltipValue = (seriesName: string, value?: number) => {
    if (typeof value !== 'number') return '-';

    switch (seriesName) {
      case SeriesNames.Time:
        return this.formatTooltipTime(value);
      case SeriesNames.Precipitation:
        return formatPrecipitation(value, 2);
      default:
        return value.toFixed(1);
    }
  };

  /**
   * Updates the chart configuration with additional overrides specific to this widget
   */
  private updateConfig() {
    this.chartOptions = {
      ...this.chartOptions,
      tooltip: {
        ...(this.chartOptions?.tooltip || {}),
        formatter: this.weatherTooltipFormatter,
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999',
          },
          label: {
            formatter: this.axisPointerLabelFormatter,
          },
        },
        backgroundColor: '#fff',
        textStyle: {
          color: '#000',
        },
      },
    };
  }

  /**
   * Formats the analyte unit labels
   *
   * @param seriesName The name of the series.
   * @param formattedValue The formatted value for the series.
   * @returns The updated formatted value with units.
   */
  private formatAnalyteProp(seriesName: string, formattedValue: string) {
    switch (seriesName) {
      case getAnalyteProp(weatherAnalyteId.temperature, 'name'):
        formattedValue += ` ${getAnalyteProp(weatherAnalyteId.temperature, 'unit')}`;
        break;
      case getAnalyteProp(weatherAnalyteId.precipitation, 'name'):
        formattedValue += ` ${getAnalyteProp(weatherAnalyteId.precipitation, 'unit')}`;
        break;
    }
    return formattedValue;
  }

  /**
   * Formats the axis pointer label tooltip when hovering over a x axis value in time series
   *
   * @param params The parameters for the axis pointer.
   * @returns The formatted axis pointer label.
   */
  private axisPointerLabelFormatter = (params: any) => {
    const formatterParams = params as AxisPointerFormatterParams;
    const axisIndex = formatterParams.axisIndex;
    const axisDimension = formatterParams.axisDimension;

    let value = formatterParams.value;

    if (axisDimension === 'x' && axisIndex === 0) {
      return this.formatTooltipTime(Number(value));
    }

    if (axisDimension === 'y' && axisIndex === 1) {
      return formatPrecipitation(Number(value), 2);
    }

    return Number(value).toFixed(1);
  };

  /**
   * Formats the tooltip content for the chart.
   *
   * Displays date_time, temperature, and precipitation information.
   *
   * @param params - The parameters for the tooltip.
   * @returns The formatted tooltip content.
   */
  private weatherTooltipFormatter = (params: TooltipFormatterParams) => {
    const paramList = Array.isArray(params) ? params : [params];

    if (paramList.length === 0) return '';

    let tooltipText = '';
    const seriesValue = paramList[0].value;
    if (Array.isArray(seriesValue) && seriesValue.length > 0) {
      const dateTime = seriesValue[0] as number;
      tooltipText = `${this.formatTooltipTime(dateTime)}<br/>`;
    } else {
      tooltipText = '<br/>';
    }

    paramList.forEach((param) => {
      const { seriesName } = param;

      const rawValue = Array.isArray(param.value) ? (param.value[1] as number) : undefined;

      if (!seriesName) {
        return;
      }

      let formattedValue = '';
      let color = '';

      switch (seriesName) {
        case getAnalyteProp(weatherAnalyteId.temperature, 'name'):
          color = getAnalyteProp(weatherAnalyteId.temperature, 'color');
          formattedValue = this.getFormattedTooltipValue(seriesName, rawValue);
          break;
        case getAnalyteProp(weatherAnalyteId.precipitation, 'name'):
          color = getAnalyteProp(weatherAnalyteId.precipitation, 'color');
          formattedValue = this.getFormattedTooltipValue(seriesName, rawValue);
          break;
      }

      const newMarker = `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>`;

      tooltipText += `${newMarker} ${seriesName}: <strong>${this.formatAnalyteProp(
        seriesName,
        formattedValue
      )}</strong><br/>`;
    });

    return tooltipText;
  };

  /**
   * Process environmental data points and update the processed data map.
   *
   * @param processedData
   * @param dp
   * @param response
   */
  processEnvironmentalData = (
    processedData: Map<string, WindBarbChartDimensions>,
    dp: EnvironmentalDataPoint,
    response: EnvironmentalDataResponse
  ) => {
    let entry = processedData.get(dp.date_time);
    if (!entry) {
      entry = { date_time: dp.date_time };
      processedData.set(dp.date_time, entry);
    }
    this.assignAverages(dp, response, entry);
  };

  /**
   * Process query responses and update the processed data map.
   *
   * @param processedData The map to update with new data.
   * @param response The query response containing environmental data.
   * @returns True if data was processed, false if no valid data was found.
   */
  processQueryResponses = (
    processedData: Map<string, WindBarbChartDimensions>,
    response: EnvironmentalDataResponse
  ) => {
    if (response.data.length === 0) return false;

    response.data.forEach((dp: EnvironmentalDataPoint) => this.processEnvironmentalData(processedData, dp, response));

    return true;
  };

  /**
   * Populate the chart with data from the query responses.
   *
   * Handles aggregation of the data based on the date range to avoid analytes becoming cluttered.
   *
   * Supports aggregation of analyte data for different time intervals (e.g., month, week) based on
   * the selected date range.
   *
   * @param queryResponses The responses from the queries.
   */
  populateResponses = (queryResponses: EnvironmentalDataResponse[], dataSources: BasicChartDataSource[]) => {
    const newChartOptions: EChartsOption = this.config.echartsConfig;
    const xAxisData: any[] = [];
    this.hasData = false;
    this.chartOptions = null;
    // Sort out the axis.
    // We can't add it to the series yet because it might have missing data points
    // Add the x axis value
    queryResponses.forEach((seriesData: any, index: number) => {
      if (seriesData.data.length > 0) {
        this.hasData = true;
      }
      seriesData.data.forEach((dataPoint: any) => {
        if (seriesData.analytes[0]?.unique_id === weatherAnalyteId.precipitation) {
          dataPoint.value = dataPoint.avg * 100;
        } else {
          dataPoint.value = dataPoint.avg;
        }

        if (!xAxisData.includes(dataPoint.date_time)) {
          xAxisData.push(dataPoint.date_time);
        }
      });
    });
    // Special case - add axis data either side to show we're not just getting one point.
    if (xAxisData.length === 1) {
      if (this.startDate === this.endDate) {
        xAxisData.push(
          moment(xAxisData[xAxisData.length - 1])
            .tz('UTC')
            .add(1, 'hour')
            .format('YYYY-MM-DDTHH:00:00.000\\Z')
        );
        xAxisData.unshift(moment(xAxisData[0]).add(-1, 'hour').tz('UTC').format('YYYY-MM-DDTHH:00:00\\Z'));
      } else {
        xAxisData.push(
          moment(xAxisData[xAxisData.length - 1])
            .tz('UTC')
            .add(1, 'day')
            .format('YYYY-MM-DDT00:00:00.000\\Z')
        );
        xAxisData.unshift(moment(xAxisData[0]).add(-1, 'day').tz('UTC').format('YYYY-MM-DDT00:00:00.000\\Z'));
      }
    }
    // If padding is specified
    if (this.config.dataConfig.addPadding === true) {
      if (this.startDate === this.endDate) {
        xAxisData.push(
          moment(xAxisData[xAxisData.length - 1])
            .tz('UTC')
            .add(1, 'hour')
            .format('YYYY-MM-DDTHH:00:00.000\\Z')
        );
        xAxisData.unshift(moment(xAxisData[0]).add(-1, 'hour').tz('UTC').format('YYYY-MM-DDTHH:00:00.000\\Z'));
      } else {
        xAxisData.push(
          moment(xAxisData[xAxisData.length - 1])
            .tz('UTC')
            .add(1, 'day')
            .format('YYYY-MM-DDT00:00:00.000\\Z')
        );
        xAxisData.unshift(moment(xAxisData[0]).add(-1, 'day').tz('UTC').format('YYYY-MM-DDT00:00:00.000\\Z'));
      }
    }
    if (!this.hasData) {
      this.chartOptions = {
        title: {
          text: 'No data is available for the selected date range.',
        },
      };
    } else {
      // Set the data.
      xAxisData.sort((a: string, b: string) => (a > b ? 1 : -1));
      queryResponses.forEach((seriesData: any, index: number) => {
        const dataSource: BasicChartDataSource = dataSources[index];
        // Build time series data: each item is [date_time, value]
        const timeSeriesData = xAxisData.map((dateTimeValue: string) => {
          const dataPoint = seriesData.data.find((dp: any) => dp.date_time === dateTimeValue);
          if (dataPoint) {
            return [
              dateTimeValue,
              dataPoint[dataSource.valueField] !== undefined
                ? Number(dataPoint[dataSource.valueField].toPrecision(seriesData.analytes[0].significant_figures))
                : null,
            ];
          } else {
            // Fill missing points with null
            return [dateTimeValue, null];
          }
        });
        (newChartOptions.series as SeriesOption[])[index].data = timeSeriesData;
      });

      newChartOptions.xAxis = {
        ...(newChartOptions.xAxis || {}),
        type: 'time',
      };

      this.chartOptions = newChartOptions;
      this.updateConfig();
    }
  };

  /**
   * Assigns average values from a data point to the corresponding entry in the chart dimensions.
   *
   * @param dp The data point containing the values.
   * @param response The response object containing metadata about the data point.
   * @param entry The chart dimensions entry to update.
   */
  private assignAverages(
    dp: EnvironmentalDataPoint,
    response: EnvironmentalDataResponse,
    entry: WindBarbChartDimensions
  ) {
    const averageValue = dp.avg;
    switch (response.analytes[0]?.unique_id) {
      case weatherAnalyteId.temperature:
        entry.temperature = averageValue;
        break;
      case weatherAnalyteId.precipitation:
        entry.precipitation = averageValue;
        break;
    }
  }

  /**
   * Set the data for the chart.
   *
   * Makes a request to the backend service to obtain data for a particular date range and granularity.
   * On the initial load, calculates granularity from the full date range. This is stored to prevent
   * redundant re-fetches.
   *
   * Uses the currently selected environmental data source and analytes as defined in the data config.
   *
   */
  setWidgetData() {
    this.hasData = false;

    // If we don't have any data sources to look at, exit early.
    const applicableDataSources = this.selectDataSources(this.config.dataConfig.dataSources);

    // If there's nothing to display....
    if (applicableDataSources.length === 0) {
      return;
    }

    forkJoin(
      applicableDataSources.map((source: BasicChartDataSource) => {
        let newFinalGranularity = this.calculateGranularity(this.startDate, this.endDate);

        return this.chartService
          .getEnvironmentalDataForSourceAndAnalyteByDate(
            source.environmentalDataSourceIdentifier,
            source.analyteIdentifier,
            moment(this.startDate).format('YYYY-MM-DD'),
            moment(this.endDate).format('YYYY-MM-DD'),
            newFinalGranularity
          )
          .pipe(take(1));
      })
    ).subscribe({
      next: (queryResponses: EnvironmentalDataResponse[]) =>
        this.populateResponses(queryResponses, applicableDataSources),
      error: (err: any) => {
        console.error(err);
        this.chartOptions = {
          title: {
            text: 'No data is available for the selected date range.',
          },
        };
      },
    });
  }

  /**
   * Check if the given analytes are valid for the specified data source.
   *
   * @param analytesToCheck The list of analytes to check.
   * @param source The data source to validate against.
   * @returns True if the analytes are valid for the source, false otherwise.
   */
  analytesValid = (analytesToCheck: string[], source: BasicChartDataSource) => {
    return analytesToCheck.includes(source.analyteIdentifier);
  };
}
